import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { Popover, OverlayTrigger } from 'react-bootstrap';

const safePct  = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const META     = 90;
const fmtS     = n => Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const colorSem = v => v >= META ? '#28a745' : v >= 80 ? '#fd7e14' : '#dc3545';
const labelSem = v => v >= META ? 'Cumple'  : v >= 80 ? 'En riesgo' : 'No cumple';

// El backend siempre agrega al final una fila "Total desde ..."
const esTotalRow = d => String(d?.fecha_inicio || '').startsWith('Total');

const BarraPeriodo = ({ dato, isTotal }) => {
  const v         = safePct(dato?.pagado?.porcentaje);
  const col       = colorSem(v);
  const cobrado   = parseFloat(dato.pagado?.monto    || 0);
  const pendiente = parseFloat(dato.pendiente?.monto || 0);
  const total     = parseFloat(dato.meta?.monto      || 0);
  const pctCob    = total > 0 ? (cobrado   / total) * 100 : 0;
  const pctPend   = total > 0 ? (pendiente / total) * 100 : 0;
  const brecha    = parseFloat((Math.max(0, META - v)).toFixed(1));

  // Desglose activos vs desertados
  const cobActivos  = parseFloat(dato.producto?.monto   || 0);
  const progActivos = parseFloat(dato.activos?.monto    || 0);
  const perdida     = parseFloat(dato.perdida?.monto    || 0);
  const recuperado  = parseFloat(dato.recuperacion?.monto || 0);
  const pctActivos    = progActivos > 0 ? Math.round((cobActivos / progActivos) * 100) : 0;
  const colAct        = colorSem(pctActivos);
  const totalSOS      = perdida + recuperado;
  const pctRecuperado = totalSOS > 0 ? Math.round((recuperado / totalSOS) * 100) : 0;

  const popover = (
    <Popover id={`pop-rec-${String(dato.fecha_inicio || '').replace(/\s+/g, '-')}`} style={{ maxWidth: 300 }}>
      <Popover.Title as="h6">Desglose del programado</Popover.Title>
      <Popover.Content>
        <table className="table table-sm mb-0" style={{ fontSize: 11 }}>
          <thead>
            <tr><th>Concepto</th><th className="text-right">Monto</th><th className="text-right">%</th></tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 700 }}>
              <td>Programado total</td>
              <td className="text-right">S/ {fmtS(total)}</td>
              <td className="text-right">100%</td>
            </tr>
            <tr style={{ color: colAct }}>
              <td>↳ Cobrado (activos)</td>
              <td className="text-right">S/ {fmtS(cobActivos)}</td>
              <td className="text-right">{pctActivos}%</td>
            </tr>
            <tr style={{ color: '#555' }}>
              <td>↳ Pendiente (activos)</td>
              <td className="text-right">S/ {fmtS(progActivos - cobActivos)}</td>
              <td className="text-right">—</td>
            </tr>
            <tr style={{ color: '#dc3545' }}>
              <td>↳ Pérdida (desertados)</td>
              <td className="text-right">S/ {fmtS(perdida)}</td>
              <td className="text-right">{safePct(dato.perdida?.porcentaje)}%</td>
            </tr>
            {recuperado > 0 && (
              <tr style={{ color: '#28a745' }}>
                <td>↳ Recuperación</td>
                <td className="text-right">S/ {fmtS(recuperado)}</td>
                <td className="text-right">—</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 6, borderTop: '1px solid #eee', paddingTop: 6 }}>
          % activos = S/ {fmtS(cobActivos)} cobrado / S/ {fmtS(progActivos)} programado
        </div>
      </Popover.Content>
    </Popover>
  );

  return (
    <div style={{
      padding: '14px 16px',
      background: isTotal ? '#f1f5f9' : '#fff',
      border: `1px solid ${col}30`,
      borderLeft: `4px solid ${col}`,
      borderRadius: 8,
    }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: isTotal ? 700 : 600, color: isTotal ? '#334155' : '#444' }}>
            {isTotal
              ? `${dato.fecha_inicio.replace('Total desde ', '')} — ${dato.fecha_fin}`
              : `${dato.fecha_inicio} — ${dato.fecha_fin}`}
          </div>
          {isTotal && <small style={{ color: '#888', fontSize: 11 }}>Resumen total del período</small>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 800, color: col, lineHeight: 1 }}>{v}%</span>
          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: `${col}15`, color: col, fontWeight: 700 }}>
            {labelSem(v)}
          </span>
          {dato.enlaceMeta && (
            <a href={dato.enlaceMeta} target="_blank" rel="noopener noreferrer"
              className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 8px', fontSize: 11 }}>
              <FontAwesomeIcon icon={faChartLine} />
            </a>
          )}
        </div>
      </div>

      {/* Barra cobrado vs pendiente */}
      <div style={{ display: 'flex', height: isTotal ? 26 : 32, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
        {cobrado > 0 && (
          <div style={{
            width: `${Math.max(pctCob, 2)}%`,
            background: col,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, color: '#fff',
            whiteSpace: 'nowrap', overflow: 'hidden', paddingInline: 8,
            transition: 'width .5s ease',
          }}>
            {pctCob >= 18 && `S/ ${fmtS(cobrado)}`}
          </div>
        )}
        <div style={{
          flex: 1, background: '#e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 600, color: '#888',
          whiteSpace: 'nowrap', overflow: 'hidden', paddingInline: 8,
        }}>
          {pctPend >= 20 && `S/ ${fmtS(pendiente)} pendiente`}
        </div>
      </div>

      {/* Leyenda */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 11, color: col, fontWeight: 700 }}>
          ● Cobrado: S/ {fmtS(cobrado)} ({v}%)
        </span>
        <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>
          ○ Pendiente: S/ {fmtS(pendiente)}
        </span>
        <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
          <span style={{ fontSize: 11, color: '#aaa', cursor: 'pointer', textDecoration: 'underline dotted' }}>
            Prog.: S/ {fmtS(total)}
          </span>
        </OverlayTrigger>
        {v < META && (
          <span style={{ fontSize: 11, color: col, fontWeight: 600 }}>↓ Brecha: {brecha}%</span>
        )}
      </div>

      {/* Desglose activos / desertados */}
      {(progActivos > 0 || perdida > 0) && (
        <div style={{ marginTop: 10, borderTop: '1px dashed #e9ecef', paddingTop: 10 }}>
          <div style={{ fontSize: 10, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>
            Desglose
          </div>

          {/* Activos */}
          {progActivos > 0 && (
            <div style={{ marginBottom: 7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: '#555', fontWeight: 600 }}>● Activos</span>
                <span style={{ fontSize: 11, color: colAct, fontWeight: 700 }}>
                  {pctActivos}% — S/ {fmtS(cobActivos)} de S/ {fmtS(progActivos)}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: '#e9ecef', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, pctActivos)}%`, height: '100%', background: colAct, borderRadius: 3, transition: 'width .5s ease' }} />
              </div>
            </div>
          )}

          {/* Desertados */}
          {(perdida > 0 || recuperado > 0) && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: '#888' }}>○ Desertados (gestión SOS)</span>
                <span style={{ fontSize: 11, color: '#dc3545', fontWeight: 600 }}>
                  {pctRecuperado}% recuperado · S/ {fmtS(perdida)} pendiente
                  {recuperado > 0 && <span style={{ color: '#28a745' }}> · S/ {fmtS(recuperado)} cobrado</span>}
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: '#fde8e8', overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(pctRecuperado, pctRecuperado > 0 ? 2 : 0)}%`, height: '100%', background: '#28a745', borderRadius: 3 }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultadoRecaudacion = ({ recaudacion }) => {
  if (!recaudacion || recaudacion.length === 0)
    return <p className="text-muted mb-0">No hay datos de recaudación para el período seleccionado.</p>;

  const periodos = recaudacion.filter(d => !esTotalRow(d));
  const totalRow = recaudacion.find(d =>   esTotalRow(d));

  // Mostrar el total consolidado solo cuando hay más de 1 período (si hay 1, ya es el total)
  const mostrarTotal = periodos.length > 1 && totalRow;

  return (
    <div>
      {periodos.map((dato, i) => (
        <div key={i} style={{ marginBottom: 12 }}>
          <BarraPeriodo dato={dato} isTotal={false} />
        </div>
      ))}

      {mostrarTotal && (
        <div style={{ borderTop: '2px dashed #e2e8f0', paddingTop: 12, marginTop: 4 }}>
          <BarraPeriodo dato={totalRow} isTotal />
        </div>
      )}
    </div>
  );
};

export default ResultadoRecaudacion;
